import { Component, createRef } from 'react';
import { connect } from 'react-redux';
import Raven from 'raven-js';
import I18n from 'I18n';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import { View } from 'foundations-components/components/View';
import { Select } from 'foundations-components/transitional/components/Select';
import getTimezoneGroups from 'timezone-utils/utils/getTimezoneGroups';
import getTimezoneOptions from 'MeetingsLib/utils/getTimezoneOptions';
import getTimezoneSelectValue from 'MeetingsLib/utils/getTimezoneSelectValue';
import { INVALID_TZ_GUESS } from 'MeetingsLib/constants/timezone';
import { jsx as _jsx } from "react/jsx-runtime";
const deprecatedTimezoneMap = {
  PST8PDT: 'America/Los_Angeles'
};
const etcTimezones = ['Etc/GMT', 'Etc/GMT+1', 'Etc/GMT+2', 'Etc/GMT+3', 'Etc/GMT+4', 'Etc/GMT+5', 'Etc/GMT+6', 'Etc/GMT+7', 'Etc/GMT+8', 'Etc/GMT+9', 'Etc/GMT+10', 'Etc/GMT+11', 'Etc/GMT+12', 'Etc/GMT-1', 'Etc/GMT-2', 'Etc/GMT-3', 'Etc/GMT-4', 'Etc/GMT-5', 'Etc/GMT-6', 'Etc/GMT-7', 'Etc/GMT-8', 'Etc/GMT-9', 'Etc/GMT-10', 'Etc/GMT-11', 'Etc/GMT-12', 'Etc/GMT-13', 'Etc/GMT-14', 'Etc/UTC'];
const etcTimezoneOptions = {
  text: 'Etc',
  value: 'Etc',
  options: etcTimezones.map(tz => {
    return {
      text: tz,
      value: tz
    };
  })
};
const etcTimezoneOptionsMap = etcTimezoneOptions.options.reduce((acc, option) => {
  acc[option.value] = option.text;
  return acc;
}, {});
const getAllTimezoneOptions = () => {
  const {
    options,
    optionsMap
  } = getTimezoneOptions();
  return {
    options: [...options, etcTimezoneOptions],
    optionsMap: Object.assign({}, optionsMap, etcTimezoneOptionsMap)
  };
};
const etcRegex = new RegExp(/^Etc\//);
const ValueComponent = ({
  children
}) => {
  return /*#__PURE__*/_jsx(TruncateString, {
    textAlign: "start",
    children: children
  });
};
class TimezoneSelect extends Component {
  constructor(props) {
    super(props);
    this.inputRef = /*#__PURE__*/createRef();
    this.onTimezoneChange = ({
      target: {
        value
      }
    }) => {
      const {
        setTimezone
      } = this.props;
      setTimezone(value);
    };
    this.onInputValueChange = ({
      target: {
        value
      }
    }) => {
      if (!this.inputRef.current) return;
      if (value) {
        this.inputRef.current.setAttribute('dir', 'auto');
      } else {
        this.inputRef.current.removeAttribute('dir');
      }
    };
    this.renderInitialValue = () => {
      const {
        timezoneOptions,
        timezoneOptionsMap,
        parsedUserTimezone
      } = this.state;
      const {
        userTimezone
      } = this.props;
      const parsedTimezoneValue = timezoneOptionsMap[parsedUserTimezone] || userTimezone;
      const timezoneValue = getTimezoneSelectValue(timezoneOptions, parsedTimezoneValue);
      return timezoneValue;
    };
    this.renderAriaLabel = (timezoneOptionsMap, parsedUserTimezone) => {
      const userTimezone = timezoneOptionsMap[parsedUserTimezone];
      if (userTimezone) {
        return I18n.text('prebook.timePicker.timezoneLabel', {
          userTimezone
        });
      }
      return I18n.text('prebook.timePicker.timezoneLabelNoneDisplayed');
    };
    this.state = {
      timezoneOptions: null,
      timezoneOptionsMap: null,
      parsedUserTimezone: null
    };
  }
  componentDidMount() {
    const {
      userTimezone
    } = this.props;
    setTimeout(() => {
      let {
        options: timezoneOptions,
        optionsMap: timezoneOptionsMap
      } = getTimezoneOptions();
      let parsedUserTimezone = INVALID_TZ_GUESS;
      try {
        if (etcRegex.test(userTimezone)) {
          parsedUserTimezone = userTimezone;
          const allTimezoneOptions = getAllTimezoneOptions();
          timezoneOptions = allTimezoneOptions.options;
          timezoneOptionsMap = allTimezoneOptions.optionsMap;
          Raven.captureMessage(`\`Etc/\` timezone detected, using custom list of \`Etc/\` timezones`, {
            level: 'info',
            extra: {
              userTimezone
            }
          });
        } else {
          parsedUserTimezone = getTimezoneGroups().resolve(userTimezone);
        }
      } catch (error) {
        if (deprecatedTimezoneMap[userTimezone]) {
          parsedUserTimezone = getTimezoneGroups().resolve(deprecatedTimezoneMap[userTimezone]);
        } else {
          Raven.captureException(error, {
            extra: {
              userTimezone
            }
          });
        }
      }
      this.setState({
        timezoneOptions,
        timezoneOptionsMap,
        parsedUserTimezone
      });
    }, 0);
  }
  componentDidUpdate(prevProps) {
    const {
      activeLocale: prevActiveLocale,
      userTimezone: prevUserTimezone
    } = prevProps;
    const {
      activeLocale: currentActiveLocale,
      userTimezone: currentUserTimezone
    } = this.props;
    if (currentActiveLocale !== prevActiveLocale) {
      /*  @ts-ignore Jun-18-2025, 19:10UTC fix types  */
      const {
        timezoneOptions,
        timezoneOptionsMap
      } = getAllTimezoneOptions();
      this.setState({
        timezoneOptions,
        timezoneOptionsMap
      });
    }
    if (currentUserTimezone !== prevUserTimezone) {
      this.setState({
        parsedUserTimezone: etcRegex.test(currentUserTimezone) ? currentUserTimezone : getTimezoneGroups().resolve(currentUserTimezone)
      });
    }
  }
  render() {
    const {
      timezoneOptions,
      timezoneOptionsMap,
      parsedUserTimezone
    } = this.state;
    if (timezoneOptions === null) {
      return /*#__PURE__*/_jsx(LoadingSpinner, {
        layout: "centered"
      });
    }
    return /*#__PURE__*/_jsx(View, {
      id: "timezone-select-label",
      "aria-label": this.renderAriaLabel(timezoneOptionsMap, parsedUserTimezone),
      paddingInlineStart: "0",
      paddingBlockStart: "100",
      children: /*#__PURE__*/_jsx(Select, {
        "aria-labelledby": 'timezone-select-label',
        "data-selenium-test": "timezone-select",
        "data-test-id": "timezone-select",
        inputRef: this.inputRef,
        menuWidth: 275,
        buttonUse: "transparent",
        defaultValue: this.renderInitialValue(),
        placement: "bottom",
        onChange: this.onTimezoneChange,
        onInputValueChange: this.onInputValueChange,
        options: this.state.timezoneOptions,
        valueComponent: ValueComponent
      })
    });
  }
}
export default connect(state => ({
  activeLocale: state.activeLocale
}))(TimezoneSelect);